<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf, email } from '@vuelidate/validators';

export default {
  emits: ['exportReportMessages', 'closeExportReportModal'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedMessageStatus: [],
      selectedEmailOption: [],
      customEmail: '',
    };
  },
  validations() {
    const commonValidations = {
      customEmail: {
        required: requiredIf(this.isCustomEmailOptionSelected),
        email,
      },
    };
    return {
      ...commonValidations,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
    }),
    statusOptions() {
      return [
        'sent',
        'delivered',
        'read',
        'answered',
        'completed',
        'failed',
      ].map(id => ({
        id,
        label: this.$t(`CAMPAIGN.REPORT.STATUS.${id.toUpperCase()}`),
      }));
    },
    emailOptions() {
      return [
        {
          id: 'currentUserEmail',
          label: this.$t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.SEND_TO.CURRENT_USER'),
        },
        {
          id: 'customEmail',
          label: this.$t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.SEND_TO.CUSTOM_EMAIL'),
        },
      ];
    },
    customPaddingBottom() {
      return this.isCustomEmailOptionSelected ? '' : 'pb-4';
    },
    isCustomEmailOptionSelected() {
      return this.selectedEmailOption.id === 'customEmail';
    },
    badParams() {
      return (
        this.v$.$invalid ||
        !this.selectedMessageStatus?.length ||
        (this.isCustomEmailOptionSelected &&
          (!this.customEmail || this.v$.customEmail.$invalid))
      );
    },
  },
  mounted() {
    this.setInitialValues();
  },
  methods: {
    setInitialValues() {
      this.selectedEmailOption = this.emailOptions[0];
      this.selectedMessageStatus = this.statusOptions;
    },
    closeModal() {
      this.$emit('closeExportReportModal');
    },
    async sendReportExportToEmail() {
      const payload = {
        email_to: this.isCustomEmailOptionSelected
          ? this.customEmail
          : this.currentUser.email,
        statuses: this.selectedMessageStatus.map(status => status.id),
      };

      this.$emit('exportReportMessages', payload);
      this.closeModal();
    },
  },
};
</script>

<template>
  <div>
    <woot-modal-header
      :header-title="$t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.TITLE')"
      :header-content="$t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.DESC')"
      class="mb-4"
    />
    <div
      class="flex flex-col row justify-center content-box pb-6 px-8 my-0 mx-auto"
    >
      <div class="main-container">
        <div class="w-full flex gap-4 flex-col">
          <!-- STATUS FILTER -->
          <div class="pb-2">
            <label>{{ $t('CAMPAIGN.FILTER.STATUS_TOOLTIP') }}</label>
            <div class="multiselect-wrap--small">
              <multiselect
                v-model="selectedMessageStatus"
                class="no-margin text-xxs"
                track-by="id"
                label="label"
                selected-label
                :close-on-select="false"
                :clear-on-select="false"
                hide-selected
                multiple
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                deselect-label=""
                :options="statusOptions"
                :searchable="false"
                :allow-empty="false"
                :max-height="150"
              />
            </div>
          </div>

          <!-- EMAIL OPTION SELETOR -->
          <div :class="customPaddingBottom">
            <label>
              {{ $t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.SEND_TO.LABEL') }}
            </label>
            <div class="multiselect-wrap--small">
              <multiselect
                v-model="selectedEmailOption"
                class="no-margin text-xxs"
                track-by="id"
                label="label"
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                deselect-label=""
                :options="emailOptions"
                :searchable="false"
                :allow-empty="false"
                :max-height="150"
              />
            </div>
          </div>

          <!-- EMAIL INPUT -->
          <div v-if="isCustomEmailOptionSelected" class="pb-2">
            <woot-input
              v-model="customEmail"
              type="text"
              :placeholder="
                $t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.SEND_TO.PLACEHOLDER')
              "
              @blur="v$.customEmail.$touch"
            />
          </div>

          <!-- SEND EMAIL AND CANCEL MODAL BUTTONS -->
          <div class="flex flex-row justify-end gap-2 pb-1 w-full">
            <woot-button :disabled="badParams" @click="sendReportExportToEmail">
              {{ $t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.BUTTONS.SUBMIT') }}
            </woot-button>
            <woot-button variant="clear" @click="closeModal">
              {{ $t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.BUTTONS.CANCEL') }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
